/**
 * How loud each of the three audio paths is, as a multiplier.
 *
 * The sibling of `choice.ts` and stored the same way, in this browser rather
 * than on the account: a level is a property of the headset in front of this
 * operator, not of the person, and carrying it to their second laptop would
 * mean carrying a number chosen for different hardware.
 *
 * ONE FILE, THREE DIFFERENT CEILINGS, and they are not a style choice.
 *
 *   - The microphone is a `GainNode` we own (`gain.ts`), so it can go above
 *     unity. That is the whole point of having it: "they cannot hear me" is
 *     answered by turning the input UP, and every other control in the browser
 *     can only turn it down.
 *   - The speaker is `HTMLMediaElement.volume`, which the platform defines as
 *     0 to 1 and which throws outside it. There is no honest way to offer
 *     more, and the machine's own volume is the way up.
 *   - The ring is synthesised by `useRingtone` through a gain node of ours,
 *     so it has the same headroom as the microphone.
 *
 * The stored value is what the operator asked for. Nothing here rewrites it,
 * and `clampVolume` only ever refuses a number that could not be applied at
 * all - a value out of range, or a value that is not a number because storage
 * held something written by an older shape.
 */
import { AUDIO_KINDS, type AudioKind } from "./choice";

/** A multiplier per path, where 1 means "as loud as the source sends it". */
export type AudioVolume = Record<AudioKind, number>;

/** Nothing changed: every path is at unity. */
export const AUDIO_VOLUME_FULL: AudioVolume = { input: 1, output: 1, ring: 1 };

/**
 * The most each path may be asked for. See the note at the top of the file for
 * why the speaker's is 1 and the other two are not.
 */
export const AUDIO_VOLUME_MAX: AudioVolume = { input: 2, output: 1, ring: 2 };

const KEY = "crm.call.volume";

/**
 * A stored or typed number, brought inside this kind's range.
 *
 * Anything unusable becomes unity rather than zero. A silent microphone is the
 * one failure an operator cannot diagnose by listening, so a corrupt stored
 * value must never be able to produce one.
 */
export function clampVolume(kind: AudioKind, value: unknown): number {
  // A real number or nothing. Coercing first looked tidier and was wrong:
  // `Number(null)` is 0, so a stored field that had gone missing came back as
  // a MUTED microphone - the one setting whose failure cannot be heard.
  if (typeof value !== "number" || !Number.isFinite(value)) return 1;
  return Math.min(AUDIO_VOLUME_MAX[kind], Math.max(0, value));
}

/** What this browser was last told. Never throws: storage can be switched off. */
export function readAudioVolume(): AudioVolume {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...AUDIO_VOLUME_FULL };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { ...AUDIO_VOLUME_FULL };
    const source = parsed as Record<string, unknown>;
    const out = { ...AUDIO_VOLUME_FULL };
    // Field by field rather than all-or-nothing: a value written before the
    // ring slider existed is still a perfectly good answer about the other two.
    for (const kind of AUDIO_KINDS) {
      if (source[kind] !== undefined) out[kind] = clampVolume(kind, source[kind]);
    }
    return out;
  } catch {
    // Private mode, storage off, or a value that is not JSON. Nothing was ever
    // chosen here, which is a working answer rather than a failure.
    return { ...AUDIO_VOLUME_FULL };
  }
}

export function writeAudioVolume(next: AudioVolume): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Not being able to remember the level is no reason to refuse it: the
    // picker still applies it to the running call for this session.
  }
}

/** The slider's position, as a whole percentage. */
export const volumeToPercent = (value: number): number => Math.round(value * 100);

/** Back again, at the precision the slider actually offers. */
export const percentToVolume = (percent: number): number => percent / 100;
